import { useEffect, useState } from "react";
import { Text } from "../../../../components/common/typography";
import { Drawer } from "../../../../components/common/drawer";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import {
  adminAiChatService,
  type AdminAiChatConversationDetail,
} from "../../../api_client";
import { formatNumber, formatUSD } from "../utils/format";
import { ConversationMessage } from "./conversation-message";

interface ConversationDrawerProps {
  chatId: string;
  onClose: () => void;
}

export function ConversationDrawer({ chatId, onClose }: ConversationDrawerProps) {
  const [detail, setDetail] = useState<AdminAiChatConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminAiChatService
      .conversationDetail(chatId)
      .then((res) => {
        if (!cancelled) setDetail(res.data);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load conversation.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chatId]);

  return (
    <Drawer
      open
      close={onClose}
      title={detail?.title ?? "Conversation"}
      width={640}
      content={
        <div className="flex flex-1 flex-col overflow-y-auto">
          {error ? (
            <div className="p-6">
              <Text variant="body" tone="body" className="!text-accentError">{error}</Text>
            </div>
          ) : loading ? (
            <div className="p-6">
              <ShimmerLoader message="Loading conversation…" />
            </div>
          ) : detail ? (
            <>
              <div className="flex shrink-0 items-center gap-6 border-b border-borderDefault bg-bgLight px-6 py-3">
                <div className="min-w-0">
                  <Text variant="bodySmBold" tone="dark" className="truncate">
                    {detail.user.fullName ?? detail.user.email ?? "Unknown"}
                  </Text>
                  {detail.user.fullName && detail.user.email && (
                    <Text variant="bodyXs" tone="muted" className="truncate">{detail.user.email}</Text>
                  )}
                </div>
                <div className="ml-auto flex shrink-0 gap-4 text-right">
                  <div>
                    <Text variant="bodyXs" tone="muted">Cost</Text>
                    <Text variant="bodySmBold" tone="dark">{formatUSD(detail.totalCostUSD)}</Text>
                  </div>
                  <div>
                    <Text variant="bodyXs" tone="muted">Tokens</Text>
                    <Text variant="bodySmBold" tone="dark">{formatNumber(detail.totalTokens)}</Text>
                  </div>
                  <div>
                    <Text variant="bodyXs" tone="muted">Messages</Text>
                    <Text variant="bodySmBold" tone="dark">{formatNumber(detail.messages.length)}</Text>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 p-4">
                {detail.messages.map((msg) => (
                  <ConversationMessage key={msg.id} message={msg} />
                ))}
              </div>
            </>
          ) : null}
        </div>
      }
    />
  );
}
